import type { CustomCellRendererProps } from "ag-grid-react";
import { clsx } from "clsx";
import { FitText } from "widgets";

import style from "../styles/Cell.module.css";
import { buildNumberCandidates } from "../utils/numberCandidates.ts";
import { ApproxText } from "./ApproxText.tsx";

/**
 * Number display in narrow table columns
 *
 * UX rules:
 * - Never ellipsize a number in the middle
 * - Always keep numbers mathematically readable
 * - Prefix approximations with "~"
 *
 * Strategies (in priority order):
 *
 * Reduce precision (approximate)
 * - 12_345.6789 → ~12_345.679 / ~12_345.68 / ~12_346
 * - 0.123456 → ~0.1235 / ~0.123 / ~0.12 / ~0
 *
 * Abbreviate by magnitude (approximate)
 * - 1_234 → ~1.23K / ~1.2K
 * - 12_345 → ~12.35K / ~12.3K
 * - 1_234_567 → ~1.23M
 *
 * Remove useless decimals (exact)
 * - 1_234.00 → 1_234
 * - 12.3400 → 12.34
 *
 * Scientific notation
 * - If provided by input (string): keep as-is (e.g. "1.55e-8")
 * - If very small numeric value: show exponential (e.g. "1.55e-8"), then approximate variants "~1.55e-8"
 *
 * Forbidden:
 * - ❌ 12…45
 * - ❌ 1 23…89

 *
 * UX:
 * - Short value in the cell
 * - Full value in a tooltip (formatted; preserves raw scientific notation)
 */
export const NumberCell = (props: CustomCellRendererProps) => {
  const rawValue = props.value;
  const numberValue =
    typeof rawValue === "number" ? rawValue : typeof rawValue === "string" ? Number(rawValue) : Number.NaN;

  const candidates = Number.isFinite(numberValue)
    ? buildNumberCandidates(numberValue, typeof rawValue === "string" ? rawValue : undefined)
    : [String(rawValue ?? "")];
  const tooltipText = candidates[0] ?? String(rawValue ?? "");
  const renderText = (text: string) => <ApproxText text={text} />;

  return (
    <div className={clsx(style.cell, style.truncate_container, style.right_aligned)}>
      <span className={style.truncate_autosize} aria-hidden>{tooltipText}</span>
      <FitText
        candidates={candidates}
        tooltipText={tooltipText}
        renderText={renderText}
        className={clsx(style.truncate_content, style.right_aligned)}
      />
    </div>
  );
};
